import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, TextField,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab, Chip, Tooltip, MenuItem, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Translate, CheckCircle, RadioButtonUnchecked, Refresh } from '@mui/icons-material';
import { cmsService } from '../../services';

const TABLES = [
  { value: 'cms_paginas', label: 'Páginas', icon: '📄' },
  { value: 'cms_hero', label: 'Hero (Cabeçalhos)', icon: '🎯' },
  { value: 'cms_features', label: 'Funcionalidades', icon: '⭐' },
  { value: 'cms_beneficios', label: 'Benefícios', icon: '✨' },
  { value: 'cms_passos', label: 'Passos', icon: '🪜' },
  { value: 'cms_faq', label: 'FAQ', icon: '❓' },
  { value: 'cms_testemunhos', label: 'Testemunhos', icon: '💬' },
  { value: 'cms_cta', label: 'CTAs', icon: '🎯' },
  { value: 'cms_planos', label: 'Planos', icon: '💰' },
  { value: 'cms_plano_features', label: 'Features de Planos', icon: '✓' },
  { value: 'cms_trust_badges', label: 'Trust Badges', icon: '🏅' },
  { value: 'cms_blog_artigos', label: 'Artigos Blog', icon: '📰' },
  { value: 'cms_blog_categorias', label: 'Categorias Blog', icon: '🏷️' },
];

const CMSTranslations: React.FC = () => {
  const [activeTable, setActiveTable] = useState(TABLES[0].value);
  const [rows, setRows] = useState<any[]>([]);
  const [fields, setFields] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editDialog, setEditDialog] = useState(false);
  const [editingRow, setEditingRow] = useState<any>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await cmsService.getTranslations(activeTable);
      setRows(res.data.data || []);
      setFields(res.data.fields || []);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar traduções');
    } finally {
      setLoading(false);
    }
  }, [activeTable]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const isTranslated = (row: any): { en: boolean; fr: boolean } => {
    const en = fields.every(f => row[`${f}_en`] && row[`${f}_en`].trim().length > 0);
    const fr = fields.every(f => row[`${f}_fr`] && row[`${f}_fr`].trim().length > 0);
    return { en, fr };
  };

  const handleEdit = (row: any) => {
    setEditingRow(row);
    const initial: Record<string, string> = {};
    for (const f of fields) {
      initial[`${f}_en`] = row[`${f}_en`] || '';
      initial[`${f}_fr`] = row[`${f}_fr`] || '';
    }
    setEditValues(initial);
    setEditDialog(true);
  };

  const handleSave = async () => {
    if (!editingRow) return;
    setSaving(true);
    try {
      await cmsService.updateTranslation(activeTable, editingRow.id, editValues);
      setSuccess('Tradução guardada com sucesso!');
      setEditDialog(false);
      setEditingRow(null);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Erro ao guardar tradução');
    } finally {
      setSaving(false);
    }
  };

  const truncate = (s: string, n = 60) => s && s.length > n ? s.substring(0, n) + '...' : s || '';

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold" display="flex" alignItems="center" gap={1}>
            <Translate /> Traduções CMS
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gerencie as traduções de todos os conteúdos do CMS para Inglês e Francês
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData} disabled={loading}>
          Atualizar
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Table selector */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={activeTable}
          onChange={(_, v) => setActiveTable(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {TABLES.map(t => (
            <Tab key={t.value} value={t.value} label={`${t.icon} ${t.label}`} />
          ))}
        </Tabs>
      </Card>

      {/* Content */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={10}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Card><CardContent sx={{ textAlign: 'center', py: 10 }}>
          <Typography color="text.secondary">Nenhum item para traduzir nesta tabela.</Typography>
        </CardContent></Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={60}>ID</TableCell>
                <TableCell>Conteúdo (PT)</TableCell>
                <TableCell width={100} align="center">🇬🇧 EN</TableCell>
                <TableCell width={100} align="center">🇫🇷 FR</TableCell>
                <TableCell width={80} align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => {
                const status = isTranslated(row);
                const mainField = fields[0];
                return (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {truncate(row[mainField])}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {status.en ? (
                        <Tooltip title="Traduzido"><CheckCircle fontSize="small" color="success" /></Tooltip>
                      ) : (
                        <Tooltip title="Por traduzir"><RadioButtonUnchecked fontSize="small" color="disabled" /></Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {status.fr ? (
                        <Tooltip title="Traduzido"><CheckCircle fontSize="small" color="success" /></Tooltip>
                      ) : (
                        <Tooltip title="Por traduzir"><RadioButtonUnchecked fontSize="small" color="disabled" /></Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar traduções">
                        <IconButton size="small" color="primary" onClick={() => handleEdit(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => !saving && setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Translate /> Editar Traduções — ID #{editingRow?.id}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            {fields.map(field => (
              <Box key={field} mb={4}>
                <Typography variant="overline" fontWeight="bold" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {field.replace(/_/g, ' ').toUpperCase()}
                </Typography>

                {/* PT - Original (read-only display) */}
                <Box mb={2} p={2} bgcolor="action.hover" borderRadius={1} border="1px solid" borderColor="divider">
                  <Typography variant="caption" fontWeight="bold" color="primary" sx={{ display: 'block', mb: 0.5 }}>
                    🇨🇻 Português (original)
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {editingRow?.[field] || <em style={{ color: '#999' }}>(vazio)</em>}
                  </Typography>
                </Box>

                {/* EN */}
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="🇬🇧 English"
                  value={editValues[`${field}_en`] || ''}
                  onChange={(e) => setEditValues(prev => ({ ...prev, [`${field}_en`]: e.target.value }))}
                  placeholder="Translate to English..."
                  sx={{ mb: 1.5 }}
                />

                {/* FR */}
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="🇫🇷 Français"
                  value={editValues[`${field}_fr`] || ''}
                  onChange={(e) => setEditValues(prev => ({ ...prev, [`${field}_fr`]: e.target.value }))}
                  placeholder="Traduire en français..."
                />
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)} disabled={saving}>Cancelar</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saving}
          >
            {saving ? 'A guardar...' : 'Guardar Traduções'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CMSTranslations;
